import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';

const CanvasContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: -50;
  overflow: hidden;
  pointer-events: none;
`;

const ParticleCanvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;

interface Particle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  size: number;
  opacity: number;
  color: string;
  type: 'core' | 'energy' | 'geometric';
  rotation: number;
  rotationSpeed: number;
  pulsePhase: number;
  connections: number[];
}

const CinematicParticles: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const particlesRef = useRef<Particle[]>([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const scrollRef = useRef(0);

  const colors = useMemo(() => ({
    primary: '#00ff88',
    secondary: '#00cc6a', 
    accent: '#88ffcc',
    energy: '#44ff99',
    core: '#ffffff'
  }), []);

  const initializeParticles = useCallback((width: number, height: number) => {
    const particles: Particle[] = [];
    const particleCount = Math.min(150, Math.floor((width * height) / 8000));

    for (let i = 0; i < particleCount; i++) {
      const type = i < particleCount * 0.1 ? 'core' : 
                   i < particleCount * 0.4 ? 'energy' : 'geometric';
      
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        z: Math.random() * 100 + 50,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.3,
        vz: (Math.random() - 0.5) * 0.2,
        size: type === 'core' ? Math.random() * 4 + 6 : 
              type === 'energy' ? Math.random() * 3 + 2 : 
              Math.random() * 2 + 1,
        opacity: type === 'core' ? Math.random() * 0.8 + 0.2 :
                 type === 'energy' ? Math.random() * 0.6 + 0.3 :
                 Math.random() * 0.4 + 0.1,
        color: type === 'core' ? colors.core :
               type === 'energy' ? colors.energy :
               Math.random() > 0.5 ? colors.primary : colors.secondary,
        type,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.02,
        pulsePhase: Math.random() * Math.PI * 2,
        connections: []
      });
    }

    // Create connections between nearby particles
    particles.forEach((particle, i) => {
      const nearbyParticles = particles
        .map((p, j) => ({ particle: p, index: j, distance: Math.hypot(particle.x - p.x, particle.y - p.y) }))
        .filter(({ distance, index }) => distance < 120 && index !== i)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 3)
        .map(({ index }) => index);
      
      particle.connections = nearbyParticles;
    });

    return particles;
  }, [colors]);

  const drawParticle = useCallback((
    ctx: CanvasRenderingContext2D,
    particle: Particle,
    time: number
  ) => {
    const { x, y, z, size, opacity, color, type, rotation, pulsePhase } = particle;
    
    // Calculate depth effect
    const scale = 100 / (100 + z);
    const actualSize = size * scale;
    const actualOpacity = opacity * scale;

    // Pulse effect
    const pulse = Math.sin(time * 0.003 + pulsePhase) * 0.3 + 0.7;

    ctx.save();
    ctx.globalAlpha = actualOpacity * pulse;
    ctx.translate(x, y);
    ctx.rotate(rotation);

    if (type === 'core') {
      // Core particles - bright glowing circles with rings
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, actualSize);
      gradient.addColorStop(0, color);
      gradient.addColorStop(0.4, color + '80');
      gradient.addColorStop(1, color + '00');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(0, 0, actualSize, 0, Math.PI * 2);
      ctx.fill();

      // Outer ring
      ctx.strokeStyle = color + '40';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(0, 0, actualSize * 1.5, 0, Math.PI * 2);
      ctx.stroke();

    } else if (type === 'energy') {
      // Energy particles - diamond shapes with glow
      ctx.fillStyle = color + Math.floor(actualOpacity * 255).toString(16).padStart(2, '0');
      
      ctx.beginPath();
      ctx.moveTo(0, -actualSize);
      ctx.lineTo(actualSize * 0.7, 0);
      ctx.lineTo(0, actualSize);
      ctx.lineTo(-actualSize * 0.7, 0);
      ctx.closePath();
      ctx.fill();

      // Glow effect
      ctx.shadowColor = color;
      ctx.shadowBlur = actualSize * 2;
      ctx.fill();

    } else {
      // Geometric particles - hexagons and triangles
      const sides = Math.random() > 0.5 ? 6 : 3;
      const angle = (Math.PI * 2) / sides;
      
      ctx.fillStyle = color + Math.floor(actualOpacity * 180).toString(16).padStart(2, '0');
      ctx.strokeStyle = color + Math.floor(actualOpacity * 255).toString(16).padStart(2, '0');
      ctx.lineWidth = 0.5;
      
      ctx.beginPath();
      for (let i = 0; i < sides; i++) {
        const pointX = Math.cos(i * angle) * actualSize;
        const pointY = Math.sin(i * angle) * actualSize;
        if (i === 0) ctx.moveTo(pointX, pointY);
        else ctx.lineTo(pointX, pointY);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    ctx.restore();
  }, []);

  const drawConnections = useCallback((
    ctx: CanvasRenderingContext2D,
    particles: Particle[]
  ) => {
    particles.forEach((particle) => {
      particle.connections.forEach((connectionIndex) => {
        const connected = particles[connectionIndex];
        if (!connected) return;

        const distance = Math.hypot(particle.x - connected.x, particle.y - connected.y);
        const opacity = Math.max(0, 1 - distance / 120) * 0.3;
        
        if (opacity > 0.05) {
          const gradient = ctx.createLinearGradient(
            particle.x, particle.y,
            connected.x, connected.y
          );
          gradient.addColorStop(0, particle.color + Math.floor(opacity * 255).toString(16).padStart(2, '0'));
          gradient.addColorStop(0.5, colors.energy + Math.floor(opacity * 128).toString(16).padStart(2, '0'));
          gradient.addColorStop(1, connected.color + Math.floor(opacity * 255).toString(16).padStart(2, '0'));

          ctx.strokeStyle = gradient;
          ctx.lineWidth = opacity * 2;
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(connected.x, connected.y);
          ctx.stroke();
        }
      });
    });
  }, [colors.energy]);

  const updateParticles = useCallback((
    particles: Particle[],
    width: number,
    height: number,
    mouseX: number,
    mouseY: number,
    scrollY: number
  ) => {
    particles.forEach((particle) => {
      // Mouse attraction
      const mouseDistance = Math.hypot(particle.x - mouseX, particle.y - mouseY);
      if (mouseDistance < 200) {
        const force = (200 - mouseDistance) / 200 * 0.02;
        const angle = Math.atan2(mouseY - particle.y, mouseX - particle.x);
        particle.vx += Math.cos(angle) * force;
        particle.vy += Math.sin(angle) * force;
      }

      // Scroll effect
      particle.vy += scrollY * 0.001;
      particle.z += scrollY * 0.1;

      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.z += particle.vz;

      // Update rotation
      particle.rotation += particle.rotationSpeed;

      // Boundary wrapping
      if (particle.x > width + 50) particle.x = -50;
      if (particle.x < -50) particle.x = width + 50;
      if (particle.y > height + 50) particle.y = -50;
      if (particle.y < -50) particle.y = height + 50;
      
      // Z boundary
      if (particle.z > 150) particle.z = 50;
      if (particle.z < 50) particle.z = 150;

      // Damping
      particle.vx *= 0.995;
      particle.vy *= 0.995;
      particle.vz *= 0.995;
    });
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const time = Date.now();
    
    // Clear canvas with subtle fade effect
    ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
    ctx.fillRect(0, 0, width, height);

    const particles = particlesRef.current;
    
    updateParticles(
      particles,
      width,
      height,
      mouseRef.current.x,
      mouseRef.current.y,
      scrollRef.current
    );

    // Draw connections first (behind particles)
    drawConnections(ctx, particles);

    // Draw particles
    particles.forEach((particle) => {
      drawParticle(ctx, particle, time);
    });

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [updateParticles, drawConnections, drawParticle]);

  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }

    particlesRef.current = initializeParticles(width, height);
  }, [initializeParticles]);

  useEffect(() => {
    handleResize();
    
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleScroll = () => {
      scrollRef.current = window.scrollY * 0.1;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [animate, handleResize]);

  return (
    <CanvasContainer>
      <ParticleCanvas ref={canvasRef} />
    </CanvasContainer>
  );
};

export default CinematicParticles;